if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        this.desktopMedia = window.matchMedia('(min-width: 750px)');
        this.handleViewportChange = () => {
          if (!this.desktopMedia.matches || this.loading) return;
          this.loading = true;
          this.loadRecommendations();
        };
        this.desktopMedia.addEventListener('change', this.handleViewportChange);
        this.handleViewportChange();
      }

      disconnectedCallback() {
        this.desktopMedia?.removeEventListener('change', this.handleViewportChange);
      }

      async loadRecommendations() {
        const rail = this.querySelector('[data-cart-drawer-upsell-rail]');
        const productIds = [...new Set((this.dataset.productIds || '').split(',').filter(Boolean))];
        const limit = Number.parseInt(this.dataset.limit, 10) || 3;

        if (!rail || productIds.length === 0) return;

        rail.setAttribute('aria-busy', 'true');
        const fallbackCards = [...rail.querySelectorAll('[data-cart-drawer-upsell-product-id]')];

        try {
          const recommendationGroups = await Promise.all(
            productIds.map((productId) => this.fetchRecommendations(productId))
          );
          const rankedProducts = new Map();

          recommendationGroups.forEach((products, groupIndex) => {
            products.forEach((product, rankIndex) => {
              const productId = String(product.id);
              if (!productId) return;

              const ranking = rankedProducts.get(productId) || {
                product,
                matches: 0,
                relevance: 0,
                firstGroup: groupIndex,
                bestRank: rankIndex,
              };
              ranking.matches += 1;
              ranking.relevance += 1 / (rankIndex + 1);
              ranking.firstGroup = Math.min(ranking.firstGroup, groupIndex);
              ranking.bestRank = Math.min(ranking.bestRank, rankIndex);
              rankedProducts.set(productId, ranking);
            });
          });

          // Products recommended for several cart items rank first. Shopify's rank,
          // then cart-item order, resolves ties.
          const selectedCards = [...rankedProducts.values()]
            .sort(
              (a, b) =>
                b.matches - a.matches ||
                b.relevance - a.relevance ||
                a.bestRank - b.bestRank ||
                a.firstGroup - b.firstGroup
            )
            .slice(0, limit)
            .map(({ product }) => this.buildCard(product, fallbackCards[0]))
            .filter(Boolean);
          const selectedProductIds = new Set(
            selectedCards.map((card) => card.dataset.cartDrawerUpsellProductId)
          );

          // Fill any empty slots from the configured fallback collection.
          fallbackCards.forEach((card) => {
            const productId = card.dataset.cartDrawerUpsellProductId;
            if (!productId || selectedProductIds.has(productId) || selectedCards.length >= limit) return;
            selectedProductIds.add(productId);
            selectedCards.push(card);
          });

          if (selectedCards.length > 0) rail.replaceChildren(...selectedCards);
        } catch (error) {
          console.error('Unable to load cart drawer recommendations:', error);
        } finally {
          rail.removeAttribute('aria-busy');
        }
      }

      async fetchRecommendations(productId) {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('product_id', productId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Recommendation request failed (${response.status})`);

        const data = await response.json();
        return Array.isArray(data.products) ? data.products : [];
      }

      buildCard(product, templateCard) {
        if (!templateCard) return null;

        const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
        if (!variant) return null;

        const card = templateCard.cloneNode(true);
        const productUrl = product.url || `/products/${product.handle}`;
        const productTitle = product.title || '';
        const imageUrl = variant.featured_image?.src || product.featured_image;

        card.dataset.cartDrawerUpsellProductId = String(product.id);
        card.querySelectorAll('a').forEach((link) => link.setAttribute('href', productUrl));

        const imageLink = card.querySelector('.cart-drawer-upsell__image');
        imageLink?.setAttribute('aria-label', productTitle);
        const image = imageLink?.querySelector('img');
        if (image && imageUrl) {
          image.src = imageUrl;
          image.removeAttribute('srcset');
          image.alt = productTitle;
        }

        const titleLink = card.querySelector('.cart-drawer-upsell__content a');
        if (titleLink) titleLink.textContent = productTitle;

        const price = card.querySelector('.cart-drawer-upsell__content span');
        if (price) price.textContent = this.formatMoney(variant.price);

        const form = card.querySelector('form');
        if (form) form.id = `CartDrawer-UpsellForm-${product.id}`;
        const variantInput = card.querySelector('[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.disabled = !variant.available;
        }

        const button = card.querySelector('[name="add"]');
        if (button) {
          button.disabled = !variant.available;
          button.setAttribute('aria-label', `Add ${productTitle} to cart`);
          const buttonText = button.querySelector('span');
          if (buttonText) buttonText.textContent = variant.available ? 'Add' : 'Sold out';
        }

        return card;
      }

      formatMoney(cents) {
        const amount = Number(cents) / 100;
        const format = this.dataset.moneyFormat || '${{amount}}';
        const formatNumber = (decimals, decimalMark, thousandsMark) => {
          const fixed = amount.toFixed(decimals);
          const [whole, fraction] = fixed.split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsMark);
          return fraction ? `${grouped}${decimalMark}${fraction}` : grouped;
        };

        return format
          .replace(/{{\s*amount\s*}}/, formatNumber(2, '.', ','))
          .replace(/{{\s*amount_no_decimals\s*}}/, formatNumber(0, '.', ','))
          .replace(/{{\s*amount_with_comma_separator\s*}}/, formatNumber(2, ',', '.'))
          .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/, formatNumber(0, ',', '.'));
      }
    }
  );
}
